/**
 * ExperimentMetrics - Temporary UX optimization tracking
 *
 * Use this for time-boxed questions like:
 * - "Which personas use the properties panel?"
 * - "Are users discovering the settings menu?"
 * - "Does feature X drive adoption?"
 *
 * Key differences from core metrics:
 * 1. Events are prefixed with `exp.` for easy filtering
 * 2. Experiments have expiry dates - auto-disabled after
 * 3. Can be enabled/disabled via feature flags
 * 4. Sampled by default (10%) to reduce noise
 * 5. Include persona context automatically
 *
 * @example
 * // Define experiment
 * ExperimentMetrics.define({
 *   id: 'properties-panel-usage',
 *   question: 'Which personas use the properties panel?',
 *   owner: 'chandika',
 *   expiresAt: '2026-02-10',
 * });
 *
 * // Track event (only fires if experiment active)
 * ExperimentMetrics.track('properties-panel-usage', 'panel_opened', {
 *   tabId: 'data',
 *   fieldCount: 5,
 * });
 */

import { Tracker } from './Tracker';

// ==========================================
// ACTIVE EXPERIMENTS - Edit this list
// ==========================================
const EXPERIMENTS: ExperimentDefinition[] = [
  {
    id: 'properties-panel-usage',
    question: 'Which personas use the properties panel? What drives adoption?',
    owner: 'chandika',
    expiresAt: '2026-02-10',
    sampleRate: 1.0, // 100% for this experiment
  },
  {
    id: 'settings-discovery',
    question: 'Are users finding and using diagram settings?',
    owner: 'chandika',
    expiresAt: '2026-02-10',
    sampleRate: 0.25, // 25% sampling
  },
  {
    id: 'data-fields-depth',
    question: 'How deeply do users fill person data fields?',
    owner: 'chandika',
    expiresAt: '2026-02-10',
    sampleRate: 0.5, // 50% sampling
  },
];

// ==========================================
// Types
// ==========================================

interface ExperimentDefinition {
  /** Unique experiment ID */
  id: string;
  /** The UX question we're trying to answer */
  question: string;
  /** Who owns this experiment */
  owner: string;
  /** When to auto-disable (ISO date string) */
  expiresAt: string;
  /** Sampling rate 0-1 (default 0.1 = 10%) */
  sampleRate?: number;
  /** Is this experiment disabled? */
  disabled?: boolean;
}

interface ExperimentContext {
  /** Current use case / persona context */
  useCase?: string;
  /** User plan */
  plan?: string;
  /** Session duration so far */
  sessionDurationMs?: number;
  /** Current shape count */
  shapeCount?: number;
}

// ==========================================
// Implementation
// ==========================================

class ExperimentMetricsService {
  private experiments: Map<string, ExperimentDefinition> = new Map();
  private sampleCohorts: Map<string, boolean> = new Map();
  private contextProvider: (() => ExperimentContext) | null = null;

  constructor() {
    // Register defined experiments
    for (const exp of EXPERIMENTS) {
      this.experiments.set(exp.id, exp);
    }
  }

  /**
   * Set context provider for automatic persona/plan tagging
   */
  setContextProvider(provider: () => ExperimentContext): void {
    this.contextProvider = provider;
  }

  /**
   * Define a new experiment at runtime
   */
  define(experiment: ExperimentDefinition): void {
    this.experiments.set(experiment.id, experiment);
  }

  /**
   * Track an experiment event
   * Only fires if experiment is active and user is in sample cohort
   */
  track(
    experimentId: string,
    action: string,
    properties?: Record<string, unknown>
  ): void {
    const experiment = this.experiments.get(experimentId);
    if (!experiment) {
      console.warn(`[ExperimentMetrics] Unknown experiment: ${experimentId}`);
      return;
    }

    // Check if expired
    if (this.isExpired(experiment)) {
      return;
    }

    // Check if disabled
    if (experiment.disabled) {
      return;
    }

    // Check sample cohort
    if (!this.isInSampleCohort(experimentId, experiment.sampleRate ?? 0.1)) {
      return;
    }

    // Get context
    const context = this.contextProvider?.() ?? {};

    // Fire event with experiment prefix
    Tracker.track(`exp.${experimentId}.${action}`, {
      ...properties,
      _experiment_id: experimentId,
      _experiment_question: experiment.question,
      _experiment_owner: experiment.owner,
      _expires_at: experiment.expiresAt,
      _use_case: context.useCase,
      _plan: context.plan,
      _session_duration_ms: context.sessionDurationMs,
      _shape_count: context.shapeCount,
    });
  }

  /**
   * Check if experiment is still active
   */
  isActive(experimentId: string): boolean {
    const experiment = this.experiments.get(experimentId);
    if (!experiment) return false;
    if (experiment.disabled) return false;
    return !this.isExpired(experiment);
  }

  /**
   * Get all active experiments (for debugging)
   */
  getActiveExperiments(): ExperimentDefinition[] {
    return Array.from(this.experiments.values()).filter(
      (exp) => !exp.disabled && !this.isExpired(exp)
    );
  }

  /**
   * Disable an experiment
   */
  disable(experimentId: string): void {
    const experiment = this.experiments.get(experimentId);
    if (experiment) {
      experiment.disabled = true;
    }
  }

  // --- Private helpers ---

  private isExpired(experiment: ExperimentDefinition): boolean {
    const expiryDate = new Date(experiment.expiresAt);
    return Date.now() > expiryDate.getTime();
  }

  private isInSampleCohort(experimentId: string, sampleRate: number): boolean {
    // Check cache
    if (this.sampleCohorts.has(experimentId)) {
      return this.sampleCohorts.get(experimentId)!;
    }

    // Determine cohort (stable per user per experiment)
    try {
      const key = `exp_cohort_${experimentId}`;
      const stored = localStorage.getItem(key);
      if (stored !== null) {
        const inCohort = stored === 'true';
        this.sampleCohorts.set(experimentId, inCohort);
        return inCohort;
      }

      const inCohort = Math.random() < sampleRate;
      localStorage.setItem(key, String(inCohort));
      this.sampleCohorts.set(experimentId, inCohort);
      return inCohort;
    } catch {
      return false;
    }
  }
}

// Singleton
export const ExperimentMetrics = new ExperimentMetricsService();

// ==========================================
// Convenience hooks for common patterns
// ==========================================

/**
 * Track when a panel/section is opened
 */
export function trackPanelOpen(
  experimentId: string,
  panelId: string,
  properties?: Record<string, unknown>
): void {
  ExperimentMetrics.track(experimentId, 'panel_opened', {
    panel_id: panelId,
    ...properties,
  });
}

/**
 * Track when a specific field is edited
 */
export function trackFieldEdit(
  experimentId: string,
  fieldId: string,
  properties?: Record<string, unknown>
): void {
  ExperimentMetrics.track(experimentId, 'field_edited', {
    field_id: fieldId,
    ...properties,
  });
}

/**
 * Track time spent in a section
 */
export function trackTimeInSection(
  experimentId: string,
  sectionId: string,
  durationMs: number
): void {
  ExperimentMetrics.track(experimentId, 'time_in_section', {
    section_id: sectionId,
    duration_ms: durationMs,
  });
}
